import Api from "./../config/api";
import { IBlockFilters } from "../models/RobocallBlocks/BlockedNumber";

interface IAxiosResponse {
    data: any
}

/**
 * /callcenter/admin/robocall-blocks — same middleware and permission stack as the
 * whitelist screens.
 *
 * Every mutating call carries a comment. Clearing or unblocking withdraws a suspicion, so
 * the API rejects those without one; confirming may be comment-free.
 */
export class RobocallBlocksActions {

    private static query(filters: IBlockFilters, extra: string[] = []): string {
        const query: string[] = [...extra]
        if (filters.status) { query.push(`status=${filters.status}`) }
        if (filters.source) { query.push(`source=${filters.source}`) }
        if (filters.q && filters.q !== '') { query.push(`q=${encodeURIComponent(filters.q)}`) }
        if (filters.min_score !== undefined && filters.min_score !== '') {
            query.push(`min_score=${filters.min_score}`)
        }
        if (filters.veto_conflicts) { query.push('veto_conflicts=1') }
        return query.length > 0 ? `?${query.join('&')}` : ''
    }

    static getBlocks(filters: IBlockFilters, page?: number, perPage?: number): Promise<any> {
        const extra: string[] = []
        if (page && page > 1) { extra.push(`page=${page}`) }
        if (perPage) { extra.push(`per_page=${perPage}`) }
        return Api.get("/robocall-blocks" + this.query(filters, extra))
            .then((res: IAxiosResponse) => res.data)
    }

    /** One number: current state, evidence, full history, and whether it is live in Redis. */
    static getBlock(phone: string): Promise<any> {
        return Api.get("/robocall-blocks/" + encodeURIComponent(phone))
            .then((res: IAxiosResponse) => res.data)
    }

    static getStats(days?: number): Promise<any> {
        return Api.get("/robocall-blocks/stats" + (days ? `?days=${days}` : ''))
            .then((res: IAxiosResponse) => res.data)
    }

    /** Manual block. `permanent` requires an explicit flag — everything else expires. */
    static createBlock(phone: string, comment: string, days?: number, permanent?: boolean): Promise<any> {
        const payload: any = { phone, comment }
        if (permanent) { payload.permanent = true } else if (days) { payload.days = days }
        return Api.post("/robocall-blocks", payload)
            .then((res: IAxiosResponse) => res.data)
    }

    /** candidate -> active. The human confirmation step. */
    static promote(id: number, comment: string): Promise<any> {
        return Api.post(`/robocall-blocks/${id}/promote`, { comment })
            .then((res: IAxiosResponse) => res.data)
    }

    /** Unblock / "not a robocall": releases and veto-protects so scoring cannot re-block. */
    static release(id: number, comment: string): Promise<any> {
        return Api.delete(`/robocall-blocks/${id}`, { data: { comment } })
            .then((res: IAxiosResponse) => res.data)
    }

    /** Extend or shorten. `days` is measured from now, so repeating it is idempotent. */
    static changeExpiry(id: number, comment: string, days?: number, permanent?: boolean): Promise<any> {
        const payload: any = { comment }
        if (permanent) { payload.permanent = true } else { payload.days = days }
        return Api.patch(`/robocall-blocks/${id}`, payload)
            .then((res: IAxiosResponse) => res.data)
    }

    /** One verdict and one comment over the ticked rows, applied in a single transaction. */
    static bulk(action: 'confirm' | 'clear', ids: number[], comment: string): Promise<any> {
        return Api.post("/robocall-blocks/bulk", { action, ids, comment })
            .then((res: IAxiosResponse) => res.data)
    }

    /** Current configuration, the schema behind it, the live reason dictionary and the
     *  recent change history — everything the settings screen needs, in one call. */
    static getSettings(): Promise<any> {
        return Api.get("/robocall-blocks/settings")
            .then((res: IAxiosResponse) => res.data)
    }

    /**
     * Save configuration. Only the keys present are touched, so a partial payload cannot
     * silently reset the rest. A comment is required — turning the blocker off is exactly
     * the change somebody asks about a week later.
     */
    static updateSettings(payload: any, comment: string): Promise<any> {
        return Api.put("/robocall-blocks/settings", { ...payload, comment })
            .then((res: IAxiosResponse) => res.data)
    }

    /**
     * CSV of the current view. Fetched as a blob and handed to the browser, because the
     * endpoint needs the Authorization header — a plain <a href> would arrive
     * unauthenticated and bounce to 401.
     */
    static exportCsv(filters: IBlockFilters): Promise<void> {
        return Api.get("/robocall-blocks/export" + this.query(filters), { responseType: 'blob' })
            .then((res: any) => {
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', `robocall-blocks-${new Date().toISOString().slice(0, 10)}.csv`)
                document.body.appendChild(link)
                link.click()
                link.parentNode?.removeChild(link)
                window.URL.revokeObjectURL(url)
            })
    }
}
